//import Absence from "entities/Absence";
import SelectOption from "entities/SelectOption";
import Subordinate from "entities/Subordinate";
import Gender from "../util/config/gender";

export function formatCommaSeparator(data: string): string {
  if (!data || typeof data === "undefined") {
    return "";
  }

  return data.replace(/\n/g, ",").replace(/,/g, ", ");
}

export function toCurrency(amount: number | string, symbol: string = "€"): string {
  const amountAsNumber = Number(amount);

  if (isNaN(amountAsNumber)) {
    return `${amount} ${symbol}`;
  }
  return `${amountAsNumber.toFixed(2)} ${symbol}`;
}

/**
 * Creates a comparison function to sort an array of objects by a specified key and order.
 *
 * @param {keyof T} key - The key to sort by.
 * @param {"asc" | "desc"} [order="asc"] - The order of sorting: "asc" for ascending or "desc" for descending.
 * @returns {(a: T, b: T) => number} - A comparison function for use in array sorting.
 */
export function orderObjectsArray<T extends object>(key: keyof T, order: "asc" | "desc" = "asc"): (a: T, b: T) => number {
  return (a: T, b: T): number => {
    if (!(key in a) || !(key in b)) {
      return 0;
    }

    const varA = typeof a[key] === "string" ? (a[key] as string).toUpperCase() : a[key];
    const varB = typeof b[key] === "string" ? (b[key] as string).toUpperCase() : b[key];

    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order === "desc" ? comparison * -1 : comparison;
  };
}

/**
 * Groups an array of objects by a specific property.
 *
 * @param {keyof T} key - The key by which to group the objects.
 * @returns {(array: T[]) => Record<string, T[]>} - A function that takes an array of objects and returns a record of grouped objects.
 */
export const groupObjectsBy = <T>(key: keyof T) => (array: T[]): Record<string, T[]> =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    const groupKey = String(value);
    objectsByKeyValue[groupKey] = (objectsByKeyValue[groupKey] || []).concat(obj);
    return objectsByKeyValue;
  }, {} as Record<string, T[]>);

/**
 * Converts a string to sentence case, capitalizing the first letter of the first word and setting the rest to lowercase.
 * @param {string} originalString - The string to convert to sentence case.
 * @returns {string} - The sentence-cased string, or an empty string if input is not a valid string.
 */
export const toSentenceCase = (originalString?: string): string => {
  if (typeof originalString !== "string") {
    return "";
  }
  return originalString
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word, index) => {
      return index === 0 ? word.replace(word[0], word[0].toUpperCase()) : word;
    })
    .join(" ");
};

/**
 * Groups an array of objects by a specific property.
 *
 * @param {Array<T>} items - The array of objects to group.
 * @param {keyof T} property - The property on which to group the objects.
 * @return {Array<Array<T>>} - The array of grouped objects. Each inner array represents a group of objects having the same value for the specified property.
 */
export const groupObjectsByProperty = <T extends Record<string, any>>(items: T[], property: keyof T): T[][] => {
  let i = 0;
  let value: T[keyof T];
  let index: number;
  const values: T[keyof T][] = [];
  const result: T[][] = [];

  for (let _; i < items.length; i++) {
    value = items[i][property];
    index = values.indexOf(value);
    if (index > -1) {
      result[index].push(items[i]);
    } else {
      values.push(value);
      result.push([items[i]]);
    }
  }
  return result;
};

//TODO: From here UPwards did not check what the functions do just converted to TS

/**
 * Removes duplicate objects from an array based on a specified property, ensuring only one
 * instance of each unique property value is kept.
 *
 * @param {Array<T>} objArray - The array of objects to filter.
 * @param {keyof T} prop - The property by which duplicates are identified.
 * @returns {Array<T>} A new array with duplicates removed based on the specified property.
 */
export const removeDuplicateDefinedObjects = <T extends Record<string, any>>(objArray: T[], prop: keyof T): T[] => {
  return [
    ...objArray.filter(o => !o[prop]),
    
    ...objArray.filter((obj, pos, arr) => {
      if (obj[prop] !== undefined && obj[prop] !== null) {
        return arr.findIndex(mapObj => mapObj[prop] === obj[prop]) === pos;
      }
      return false;
    })
  ];
};

/**
 * Combines two arrays of objects, ensuring that if both arrays have an object
 * with the same specified property, only the object from the second array is
 * included in the resulting array.
 *
 * @param {Array<T>} arr1 - The first array of objects.
 * @param {Array<T>} arr2 - The second array of objects.
 * @param {string} key - The property by which the objects in the arrays should be compared.
 * @returns {Array<T>} The combined array of objects.
 */
export function combineArrays<T extends Record<string, any>>(arr1: T[], arr2: T[], key: keyof T): T[] {
  if (!key) {
    return [];
  }

  const combinedArray = arr1.reduce((accumulator, obj1) => {
    const obj2 = arr2.find(obj => obj[key] === obj1[key]);

    if (obj2) {
      accumulator.push(obj2);
    } else {
      accumulator.push(obj1);
    }

    return accumulator;
  }, [] as T[]);

  const uniqueArr2Items = arr2.filter(
    obj2 => !arr1.find(obj1 => obj1[key] === obj2[key])
  );

  return combinedArray.concat(uniqueArr2Items);
}


/**
 * Function to get the key of a given value in an object.
 *
 * This function iterates over the keys of the input object
 * and returns the first key which corresponds to the provided value.
 * If no matching key is found, it will return `undefined`.
 *
 * @param {Record<string, T>} object - The object in which to look for the key.
 * @param {T} value - The value for which to find the key.
 * @return {(keyof T | undefined)} The key of the value if found, otherwise `undefined`.
 */
export const getObjectKeyByValue = <T extends Record<string, unknown>>(
  object: T,
  value: T[keyof T]  | undefined
): keyof T | undefined => {
  return Object.keys(object).find((key) => object[key as keyof T] === value) as keyof T | undefined;
};

export const base64regex =
  /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

/**
 * Checks if a string contains any numeric characters.
 * @param {string} str - The string to check.
 * @returns {boolean} - Returns true if the string contains numbers, false otherwise.
 */
export const containsNumbers = (str: string): boolean => {
  return /\d/.test(str);
};

/**
 * Checks if an object has a key-value pair that matches the specified key and value.
 * @param {Record<string, unknown>} obj - The object to search for the key-value pair.
 * @param {string} key - The key to match.
 * @param {unknown} value - The value to match.
 * @returns {boolean} - Returns true if the object contains the key-value pair, false otherwise.
 */
export function hasKeyValuePair(
  obj: Record<string, unknown>,
  key: string,
  value: unknown
): boolean {
  return obj
    ? Object.entries(obj).some(([k, v]) => k === key && v === value)
    : false;
}

/**
 * Checks if a value is a valid React component.
 * @param {*} value - The value to check.
 * @returns {boolean} - `true` if the value is a valid React component, `false` otherwise.
 */
export function isValidReactComponent(value: any) {
  return (
    value &&
    (typeof value === "function" ||
      value.$$typeof === Symbol.for("react.element"))
  );
}

/**
 * Converts a Base64-encoded string to a blob URL.
 *
 * @param {string} base64 - The Base64-encoded string to be converted.
 * @param {string} mimeType - The MIME type of the data.
 * @returns {string} - The blob URL created from the Base64-encoded string or null in case of errors.
 */
export function base64ToBlobUrl(base64: string, mimeType: string) {
  if (!base64 || typeof base64 !== "string") {
    return null;
  }

  if (!mimeType || typeof mimeType !== "string") {
    return null;
  }

  let byteCharacters;
  try {
    byteCharacters = atob(base64);
  } catch (err) {
    console.error(err);
    return null;
  }

  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  return URL.createObjectURL(blob);
}

/**
 * Checks if a value is a number or a string that can be converted to a number.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if the value is a number or can be converted to one, otherwise `false`.
 *
 */
export function isNumberOrConvertible(value: any) {
  if (typeof value === "number" && !isNaN(value)) {
    return true;
  }

  if (typeof value === "string") {
    const converted = Number(value);
    return !isNaN(converted);
  }

  return false;
}

/**
 * Checks if two arrays contain the same elements, regardless of order.
 *
 * @param {number[]} arr1 - The first array to compare.
 * @param {number[]} arr2 - The second array to compare.
 * @returns {boolean} - Returns true if both arrays contain the same elements, otherwise false.
 */
export function sameArrayElements(arr1: number[], arr2: number[]) {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = arr1.slice().sort((a, b) => a - b);
  const sortedArr2 = arr2.slice().sort((a, b) => a - b);

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) return false;
  }

  return true;
}

/**
 * Extracts the birthday from a given personal identification number.
 *
 * @param {string} idNumber - The 11-digit personal identification number.
 * @returns {string} - The date of birth as a string, or undefined if invalid.
 */
export function getBirthdayFromIdentificationNumber(idNumber: string) {
  if (idNumber.length !== 11) {
    return undefined;
  }

  // Extracting the century
  const centuryDigit = parseInt(idNumber.charAt(0));
  let century;
  if (centuryDigit === 1 || centuryDigit === 2) {
    century = 1800;
  } else if (centuryDigit === 3 || centuryDigit === 4) {
    century = 1900;
  } else if (centuryDigit === 5 || centuryDigit === 6) {
    century = 2000;
  } else {
    return undefined;
  }

  // Extracting the date of birth
  const year = century + parseInt(idNumber.slice(1, 3));
  const month = parseInt(idNumber.slice(3, 5));
  const day = parseInt(idNumber.slice(5, 7));

  // Formatting the date as "YYYY-MM-DD"
  const formattedDate = `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  return formattedDate;
}

/**
 * Extracts the gender from a given personal identification number.
 *
 * @param {string} idNumber - The 11-digit personal identification number.
 * @returns {Gender} - The gender ("Male" enum or "Female" enum) or "Unknown" enum if invalid.
 */
export function getGenderFromIdentificationNumber(idNumber: string) {
  if (idNumber.length !== 11) {
    return Gender.Unknown;
  }

  const genderDigit = parseInt(idNumber.charAt(0));
  if (isNaN(genderDigit)) {
    return Gender.Unknown;
  }

  return genderDigit % 2 === 0 ? Gender.Female : Gender.Male;
}

/**
 * Calculates the age based on the given birthdate.
 *
 * @param {string| Date} dateString The birthdate.
 * @return {number} The age in years as an integer.
 */
export function calculateAge(dateString: string | Date) {
  const birthDate = new Date(dateString);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

/**
 * Create an array of options for react-select library. Each option is an object with 'value' and 'label' properties.
 * 'value' is the unique identifier and 'label' is its name that will be shown to the user.
 * This function ignores duplicate objects by only processing the first instance of each unique id and filters out items with undefined Ids.
 *
 * @param {Array<T>} items - Array of objects from which to create options.
 * @param {keyof T} [valueKey='Id'] - Optional key to use for the option's value.
 * @param {keyof T} [labelKey='Name'] - Optional key to use for the option's label.
 * @returns {SelectOption[]} An array of option objects with 'value' and 'label' properties.
 */
export function createSelectOptions<T>(
  items: T[],
  valueKey?: keyof T,
  labelKey?: keyof T
): SelectOption[] {
    const data: SelectOption[] = [];
  const seenValues = new Set<string | number>();

  // Set default keys if not provided
  const actualValueKey = (valueKey || 'Id') as keyof T;
  const actualLabelKey = (labelKey || 'Name') as keyof T;

  for (const item of items) {
    const value = item[actualValueKey];
    const label = item[actualLabelKey];

    // Ensure value is not null or undefined and is of type string or number
    if (
      (typeof value === 'string' || typeof value === 'number') &&
      value != null &&
      !seenValues.has(value)
    ) {
      data.push({
        value,
        label: typeof label === 'string' ? label : String(label || ''),
      });
      seenValues.add(value);
    }
  }

  return data.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Maps an array of subordinates to an array of options with 'Id' and 'Name' properties.
 * By default, it uses 'OrganizationUnitId' as 'Id' and 'OrganizationUnitName' as 'Name'.
 * 
 * @param {Subordinate[]} subordinates - The array of subordinate objects to map.
 * @param {keyof Subordinate} [idField='OrganizationUnitId'] - The field in Subordinate to use as the 'Id' for each option. OrganizationUnitId is default
 * @param {keyof Subordinate} [nameField='OrganizationUnitName'] - The field in Subordinate to use as the 'Name' for each option. OrganizationUnitName is default
 * @returns {Array<{ Id: number | string, Name: string }>} - An array of objects suitable for select options.
 */
export const mapSubordinatesToOptions = (
  subordinates: Subordinate[],
  idField: keyof Subordinate = 'OrganizationUnitId', 
  nameField: keyof Subordinate = 'OrganizationUnitName' 
) => {
  return subordinates
    .filter(subordinate => subordinate[idField] != null) 
    .map(subordinate => ({
      Id: subordinate[idField] as number | string, 
      Name: subordinate[nameField] as string, 
    }));
};

/**
 * Determines if an absence is ongoing or has been calculated.
 *
 * This function checks if the provided absence has started and is currently ongoing,
 * or if it is marked as read-only (has been already calculated). If the absence has a start date that is less than or equal
 * to the current date, it is considered ongoing. Alternatively, if the absence is marked as read-only,
 * it is considered calculated.
 *
 * @param {Object} absence - The absence object to check.
 * @param {string} absence.StartDate - The start date of the absence in a format recognized by the Date constructor.
 * @param {boolean} absence.ReadOnly - A flag indicating if the absence is read-only.
 * @returns {boolean} - Returns `true` if the absence is ongoing or read-only, otherwise returns `false`.
 */
//export function absenceIsOngoingOrCalculated(absence: Absence) {
//  if (!absence?.StartDate || typeof absence !== "object") {
//    return false;
//  }

//  const startDate = new Date(absence.StartDate);
//  const now = new Date();

//  const isOngoing = startDate <= now;

//  return isOngoing || absence.ReadOnly;
//}

/**
 * Extracts unique values of a specified property from an array of objects.
 *
 * @param {Array<T>} items - Array of objects containing the specified property.
 * @param {keyof T} property - The property name to extract unique values from.
 * @returns {Array<T[keyof T]>} Array of unique values for the specified property from the input objects.
 */
export function getUniquePropertyValues<T, K extends keyof T>(items: Array<T>, property: K): Array<T[K]> {
  const values = items.map(item => item[property]);
  return Array.from(new Set(values));
}

export function getEnumValueByKeySafe<T extends object>(enumObj: T, key: unknown): T[keyof T] | undefined {
    if (typeof key === "string"  && key in enumObj) {
        return enumObj[key as keyof T];
    }
    if (typeof key === "number" && key in enumObj) {
        return (key as T[keyof T]);
    }
    return undefined;
}

export const getEnumValueByKeyStrict = <T extends object>(
  enumObj: T,
  key: keyof T
): T[keyof T] => {
  const value = enumObj[key];
  return enumObj[key];
};
